import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { formatDate } from 'date-fns'
import { Filter } from 'lucide-react'
import React, { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'

import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { brokersMembers } from 'rails_routes'
import { Badge } from 'slab'
import {
  useGetBrokersCoverageAuditLogs,
  usePatchBrokersCoverageAuditLogsAuditLogId,
} from 'utils/web_api'
import {
  type BrokersCoverageAuditLog, type GetBrokersCoverageAuditLogsParams,
  type PatchBrokersCoverageAuditLogsAuditLogIdReason
} from 'utils/web_api.schemas'

// Keep the original clean filter interface
export type CoverageAuditLogsFilters = {
  member_ids?: string[]
  enrollment_id?: string
  target_id?: string
  reasons?: string[]
  actions?: string[]
  target_types?: string[]
  after_date?: string
  before_date?: string
  created_by_broker_id?: string[]
  corrected_by_broker_id?: string[]
} & Record<string, string | string[]>

type GetCoverageAuditLogsQueryArgs = {
  filters: CoverageAuditLogsFilters
  pagination: PaginationState
  sorting: SortingState
}

// Convert from clean interface to API format
const convertToApiParams = ({ filters, pagination, sorting }: GetCoverageAuditLogsQueryArgs): GetBrokersCoverageAuditLogsParams => {
  const params: GetBrokersCoverageAuditLogsParams = {
    page: pagination.pageIndex,
    items: pagination.pageSize,
    'filters[member_ids][]': filters.member_ids,
    'filters[enrollment_id]': filters.enrollment_id,
    'filters[target_id]': filters.target_id,
    'filters[reasons][]': filters.reasons,
    'filters[actions][]': filters.actions,
    'filters[target_types][]': filters.target_types,
    'filters[after_date]': filters.after_date,
    'filters[before_date]': filters.before_date,
    'filters[created_by_broker_id][]': filters.created_by_broker_id,
    'filters[corrected_by_broker_id][]': filters.corrected_by_broker_id,
  }

  if (sorting.length > 0) {
    (params as any)[`sort[${sorting[0].id}]`] = sorting[0].desc ? 'desc' : 'asc'
  }

  return params
}

export const getCoverageAuditLogsQueryKey = ({ filters, pagination, sorting }: GetCoverageAuditLogsQueryArgs) => {
  return [
    'brokers',
    'coverage_audit_logs',
    'filters',
    filters,
    'pagination',
    pagination,
    'sorting',
    sorting,
  ]
}

interface CoverageAuditLogsTableProps {
  filters: CoverageAuditLogsFilters
  setFilters: Dispatch<SetStateAction<CoverageAuditLogsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  availableReasons?: string[]
}

const columnHelper = createColumnHelper<BrokersCoverageAuditLog>()

const ActionBadge = ({ action }: { action: string }) => {
  switch (action) {
    case 'create':
      return <Badge type='success'>Create</Badge>
    case 'update':
      return <Badge type='info'>Update</Badge>
    case 'destroy':
      return <Badge type='critical'>Destroy</Badge>
    default:
      return <Badge type='neutral'>{action}</Badge>
  }
}

const formatTargetInfo = (row: BrokersCoverageAuditLog) => {
  const targetType = row.target_type

  if (!targetType) return 'Unknown Target'

  // Extract the class name from the full module path
  const className = targetType.split('::').pop() || targetType

  switch (className) {
    case 'Premium': {
      // Use target_details if available from API
      if (row.target_details) {
        return `Premium (${row.target_details})`
      }
      // Fallback: Try to extract month from audited_changes if it contains period_start_date
      const changes = row.audited_changes as Record<string, [any, any]>
      if (changes.period_start_date) {
        const [oldDate, newDate] = changes.period_start_date
        const dateToUse = newDate || oldDate
        if (dateToUse) {
          const month = formatDate(new Date(dateToUse), 'MMMM yyyy')
          return `Premium (${month})`
        }
      }
      return 'Premium'
    }
    case 'Enrollment':
      return 'Enrollment'
    case 'EnrollmentCoverage':
      // Use target_details if available from API
      if (row.target_details) {
        return `Participant coverage (${row.target_details})`
      }
      return 'Participant coverage'
    default:
      return className
  }
}

const CoverageAuditLogsTable: React.FC<CoverageAuditLogsTableProps> = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  availableReasons,
}) => {
  const { data, isLoading, isError, refetch } = useGetBrokersCoverageAuditLogs(
    convertToApiParams({ filters, pagination, sorting })
  )
  const updateReasonMutation = usePatchBrokersCoverageAuditLogsAuditLogId()

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Memoize the columns to avoid re-rendering the table
  const columns = useMemo(() => {
    return [
      columnHelper.accessor('created_at', {
        header: (info) => (
          <div className='w-28'>
            <SortableHeader<BrokersCoverageAuditLog>
              {...info}
              headerText='Changed On'
            />
          </div>
        ),
        cell: (info) => {
          const date = new Date(info.getValue())
          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
          })
          return (
            <div className='whitespace-nowrap'>
              <div>{dateStr}</div>
              <div className='text-gray-600 text-sm'>{timeStr}</div>
            </div>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('member_name', {
        header: 'Member',
        cell: ({ row }) => {
          const memberName = row.original.member_name
          const memberId = row.original.member_id

          if (!memberName || !memberId) return 'N/A'

          return (
            <div className='flex items-center gap-1'>
              <a
                href={brokersMembers.show({ id: memberId })}
                className='text-blue-600 hover:text-blue-800 underline'
              >
                {memberName}
              </a>
              <button
                onClick={() => setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, member_ids: [memberId] }))}
                className='text-gray-400 hover:text-gray-600'
                title='Filter by this member'
              >
                <Filter size={14} />
              </button>
            </div>
          )
        },
      }),
      columnHelper.accessor('enrollment_title', {
        header: 'Enrollment',
        cell: ({ row }) => {
          const title = row.original.enrollment_title
          const enrollmentId = row.original.insurance_enrollment_id

          if (!title) return 'N/A'

          const truncated = title.length > 12 ? `${title.substring(0, 12)}...` : title
          return (
            <div className='flex items-center gap-1'>
              <span title={title}>
                {truncated}
              </span>
              <button
                onClick={() => setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, enrollment_id: enrollmentId }))}
                className='text-gray-400 hover:text-gray-600'
                title='Filter by this enrollment'
              >
                <Filter size={14} />
              </button>
            </div>
          )
        },
      }),
      columnHelper.accessor('created_by_broker.broker_name', {
        header: 'Created By',
        cell: ({ row }) => {
          const brokerId = row.original.created_by_broker?.id
          const brokerName = row.original.created_by_broker?.broker_name

          if (!brokerId) {
            return (
              <div className='flex items-center gap-1'>
                <span>System</span>
                <button
                  onClick={() => setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, created_by_broker_id: ['system'] }))}
                  className='text-gray-400 hover:text-gray-600'
                  title='Filter by System'
                >
                  <Filter size={14} />
                </button>
              </div>
            )
          }

          return (
            <div className='flex items-center gap-1'>
              <span>{brokerName}</span>
              {brokerId && (
                <button
                  onClick={() => setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, created_by_broker_id: [brokerId] }))}
                  className='text-gray-400 hover:text-gray-600'
                  title='Filter by this broker'
                >
                  <Filter size={14} />
                </button>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: (info) => <ActionBadge action={info.getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor('reason', {
        header: 'Reason',
        cell: ({ row }) => {
          const currentReason = row.original.reason

          if (!availableReasons || availableReasons.length === 0) {
            return (
              <span className='inline-block min-w-[150px]'>
                {currentReason
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </span>
            )
          }

          const isUpdating = updateReasonMutation.isPending
          const originalReason = row.original.original_reason
          const correctedByBrokerId = row.original.corrected_by_broker?.id
          const correctedByBrokerName = row.original.corrected_by_broker?.broker_name

          return (
            <div className='space-y-1'>
              <select
                value={currentReason}
                onChange={(e) => {
                  const newReason = e.target.value as PatchBrokersCoverageAuditLogsAuditLogIdReason
                  updateReasonMutation.mutate(
                    {
                      auditLogId: row.original.id,
                      data: { reason: newReason },
                    },
                    {
                      onSuccess: () => {
                        refetch()
                      },
                    }
                  )
                }}
                disabled={isUpdating}
                className='block w-full min-w-[200px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:opacity-50'
              >
                {availableReasons?.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                  </option>
                ))}
              </select>
              {originalReason && originalReason !== currentReason && (
                <div className='text-xs text-gray-500'>
                  Original: {originalReason
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
                </div>
              )}
              {correctedByBrokerId && (
                <div className='text-xs text-gray-500 flex items-center gap-1'>
                  <span>Corrected by: {correctedByBrokerName}</span>
                  <button
                    onClick={() => setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, corrected_by_broker_id: [correctedByBrokerId] }))}
                    className='text-gray-400 hover:text-gray-600'
                    title='Filter by this broker'
                  >
                    <Filter size={12} />
                  </button>
                </div>
              )}
            </div>
          )
        },
        enableSorting: false,
      }),
      columnHelper.accessor('audited_changes', {
        header: 'Changes',
        cell: ({ row }) => {
          const changes = row.original.audited_changes as Record<string, [any, any]>
          const targetInfo = formatTargetInfo(row.original)

          return (
            <div className='text-xs space-y-1'>
              <div className='font-medium flex items-center gap-1'>
                <span>Target: {targetInfo}</span>
                {row.original.target_id && (
                  <button
                    onClick={() => {
                      const targetId = row.original.target_id
                      if (targetId) {
                        setFilters((prev: CoverageAuditLogsFilters) => ({ ...prev, target_id: targetId }))
                      }
                    }}
                    className='text-gray-400 hover:text-gray-600'
                    title='Filter by this target'
                  >
                    <Filter size={12} />
                  </button>
                )}
              </div>
              {Object.keys(changes).length > 0 && (
                <>
                  <div className='font-medium'>Changes:</div>
                  <ul className='list-disc list-inside pl-2'>
                    {Object.entries(changes).map(([key, [oldVal, newVal]]) => (
                      <li key={key}>
                        <span className='font-medium'>{key}:</span> {String(oldVal)} → {String(newVal)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )
        },
      }),
    ]
  }, [availableReasons, updateReasonMutation, refetch])

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='No coverage audit logs found.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='audit log'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 1,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 1,
        })}
    />
  )
}

export default CoverageAuditLogsTable
